import React, { useState, FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../utils/axios';
import { passwordResetSchema } from '../utils/validationSchemas';
import { useFormValidation } from '../hooks/useFormValidation';
import { ErrorMessage } from '../components/ErrorMessage';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    password: '',
    confirmPassword: ''
  });

  const token = searchParams.get('token');

  const { errors, validateField, validateForm } = useFormValidation(passwordResetSchema);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name as keyof ResetPasswordFormData, value);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!token) {
      toast.error('Invalid reset link');
      return;
    }

    if (!validateForm(formData)) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/members/reset-password', {
        token,
        password: formData.password
      });

      if (response.data.success) {
        setSuccess(true);
        toast.success('Password reset successfully!');

        setTimeout(() => {
          navigate('/member/login');
        }, 3000);
      }
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to reset password. The link may have expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-8 px-4 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-[#C49A3C]/20 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-[#0D5128] p-3 rounded-2xl">
              <img src="/website-04.png" alt="Hammad Foundation" className="h-12 w-auto object-contain" />
            </div>
          </div>
          <div className="flex justify-center mb-4">
            <Lock size={48} className="text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#0D5128] mb-3">Invalid Reset Link</h1>
          <p className="text-sm text-[#0D5128]/70 mb-6">
            This password reset link is invalid or has expired.
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center justify-center w-full bg-[#0D5128] hover:bg-[#0a3e1f] text-white py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl no-underline"
          >
            Request New Reset Link
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-8 px-4 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-[#C49A3C]/20 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-[#0D5128] p-3 rounded-2xl">
              <img src="/website-04.png" alt="Hammad Foundation" className="h-12 w-auto object-contain" />
            </div>
          </div>
          <div className="flex justify-center mb-4">
            <CheckCircle size={48} className="text-[#0D5128]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0D5128] mb-3">Password Reset Successfully!</h1>
          <p className="text-sm text-[#0D5128]/70 mb-2">
            Your password has been reset. You can now login with your new password.
          </p>
          <p className="text-xs text-[#0D5128]/50 mb-6">
            Redirecting to login page in 3 seconds...
          </p>
          <Link
            to="/member/login"
            className="inline-flex items-center justify-center w-full bg-[#0D5128] hover:bg-[#0a3e1f] text-white py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl no-underline"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-8 px-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-[#C49A3C]/20">
        <Link to="/member/login" className="inline-flex items-center gap-2 text-[#0D5128]/60 no-underline text-sm mb-6 hover:text-[#0D5128] transition-colors">
          <ArrowLeft size={18} />
          Back to Login
        </Link>

        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-[#0D5128] p-3 rounded-2xl">
              <img src="/website-04.png" alt="Hammad Foundation" className="h-12 w-auto object-contain" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-[#0D5128] mb-1">Reset Your Password</h1>
          <p className="text-sm text-[#0D5128]/60">
            Enter a new password for your account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[#0D5128] mb-2">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                  errors.password
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-[#0D5128]'
                }`}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center border-0 bg-transparent cursor-pointer"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            <ErrorMessage message={errors.password} />
            {!errors.password && (
              <p className="text-xs text-[#0D5128]/50 mt-1">
                Min 8 characters with uppercase, lowercase, and number
              </p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#0D5128] mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                  errors.confirmPassword
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-[#0D5128]'
                }`}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center border-0 bg-transparent cursor-pointer"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            <ErrorMessage message={errors.confirmPassword} />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0D5128] hover:bg-[#0a3e1f] text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl mt-2"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Lock className="h-5 w-5" />
                Reset Password
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
